import React, { useMemo } from 'react';
import { Card, Row, Col, Button, Typography } from 'antd';
import {
  FileTextOutlined,
  ShoppingOutlined,
  ShoppingCartOutlined,
  InboxOutlined,
  ToolOutlined,
  WarningOutlined,
  SolutionOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/store/authStore';
import { canAccessRouteByPermissions } from '@/config/routePermissions';
import { EnterpriseTokens } from '@/theme/enterprise-tokens';

const { Title } = Typography;

type QuickItem = { path: string; label: string; icon: React.ReactNode };

const QUICK_ITEMS: QuickItem[] = [
  { path: '/growth/estimations', label: 'New estimation', icon: <FileTextOutlined /> },
  { path: '/growth/orders/new', label: 'New customer order', icon: <ShoppingOutlined /> },
  { path: '/scm/procurement/purchase-orders/new', label: 'New purchase order', icon: <ShoppingCartOutlined /> },
  { path: '/scm/procurement/grn/new', label: 'New GRN', icon: <InboxOutlined /> },
  { path: '/scm/inventory/material-requests/new', label: 'Material request', icon: <FileTextOutlined /> },
  { path: '/mfg/work-orders/new', label: 'New work order', icon: <ToolOutlined /> },
  { path: '/fin/gl/journals/new', label: 'Journal voucher', icon: <FileTextOutlined /> },
  { path: '/fin/debit-notes/new', label: 'Debit note', icon: <FileTextOutlined /> },
  { path: '/qms/ncrs/new', label: 'New NCR', icon: <WarningOutlined /> },
  { path: '/qms/capas/new', label: 'New CAPA', icon: <SolutionOutlined /> },
];

export const ExecutiveQuickActions: React.FC = () => {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const canAccess = (path: string) =>
    canAccessRouteByPermissions(path, user?.permissions ?? [], !!user?.is_super_admin);

  const items = useMemo(() => QUICK_ITEMS.filter((q) => canAccess(q.path)), [user?.permissions, user?.is_super_admin]);

  if (items.length === 0) return null;

  return (
    <Card
      title={<Title level={5} style={{ margin: 0 }}>Quick create</Title>}
      bordered={false}
      style={{ marginBottom: EnterpriseTokens.spacing.xl, border: EnterpriseTokens.workspace.borderSubtle }}
    >
      <Row gutter={[12, 12]}>
        {items.map((q) => (
          <Col xs={24} sm={12} md={8} lg={6} key={q.path}>
            <Button block icon={q.icon} onClick={() => navigate(q.path)}>
              {q.label}
            </Button>
          </Col>
        ))}
      </Row>
    </Card>
  );
};
